import type { RecipeMeta, ShapeType } from '../../utils/parseIngredients'
import type { TargetShape } from '../../utils/calculator'

/**
 * Форма в виде, в котором её вводит пользователь: размеры хранятся строками,
 * потому что поле может быть пустым или недозаполненным.
 */
export interface ShapeValue {
  type: ShapeType
  diameter: string
  sideA: string
  sideB: string
  height: string
}

export const EMPTY_SHAPE: ShapeValue = {
  type: 'circle',
  diameter: '',
  sideA: '',
  sideB: '',
  height: '',
}

export const SHAPE_LABELS: Record<ShapeType, string> = {
  circle: 'Круг',
  square: 'Квадрат',
  rectangle: 'Прямоугольник',
}

/** Размеры формы для расчёта или null, если заполнены не все поля. */
export const toShape = (value: ShapeValue): TargetShape | null => {
  const diameter = Number(value.diameter)
  const sideA = Number(value.sideA)
  const sideB = Number(value.sideB)

  switch (value.type) {
    case 'circle':
      return diameter > 0 ? { type: 'circle', diameter } : null
    case 'square':
      return sideA > 0 ? { type: 'square', sideA } : null
    case 'rectangle':
      return sideA > 0 && sideB > 0 ? { type: 'rectangle', sideA, sideB } : null
  }
}

export const toHeight = (value: ShapeValue): number => Number(value.height) || 0

/** Готова ли форма к расчёту: есть все размеры и высота. */
export const isShapeFilled = (value: ShapeValue): boolean =>
  toShape(value) !== null && toHeight(value) > 0

/** Подпись формы для карточки результата: «Круг Ø16 см, высота 8 см». */
export const describeShape = (value: ShapeValue): string => {
  const shape = toShape(value)
  if (!shape) return '—'

  const size =
    shape.type === 'circle'
      ? `Круг Ø${shape.diameter} см`
      : shape.type === 'square'
        ? `Квадрат ${shape.sideA}×${shape.sideA} см`
        : `Прямоугольник ${shape.sideA}×${shape.sideB} см`

  const height = toHeight(value)
  return height > 0 ? `${size}, высота ${height} см` : size
}

/** Переносит форму и высоту из разобранного рецепта в поля калькулятора. */
export const fromRecipeMeta = (meta: RecipeMeta): ShapeValue => {
  const shape = meta.originalShape
  const height = meta.originalHeight ? String(meta.originalHeight) : ''
  if (!shape) return { ...EMPTY_SHAPE, height }

  return {
    type: shape.type,
    diameter: shape.diameter ? String(shape.diameter) : '',
    sideA: shape.sideA ? String(shape.sideA) : '',
    sideB: shape.sideB ? String(shape.sideB) : '',
    height,
  }
}
